import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  buildClientProfileSearch,
  buildCreateIntakeSearch,
  buildDocsOnlyIntakeSearch,
  buildIntakesBaseSearch,
  buildOpenIntakeSearch,
  parseClientProfileSearch,
  type ClientFocusParams,
  type ClientProfileUrlState,
  type ClientTab,
  type CreateIntakeOptions
} from "../urlState";

export type ClientProfileUrlApi = {
  state: ClientProfileUrlState;
  /** Switch sections; `replace` keeps the history clean for in-page nav. */
  setTab: (tab: ClientTab, focus?: ClientFocusParams) => void;
  openCreateIntake: (opts?: CreateIntakeOptions) => void;
  /** Open one request in the Requests IDE. */
  openIntake: (intakeId: string) => void;
  openDocsOnlyIntakeForVisa: (visaWorkflow: string) => void;
  /** Href for the Requests list with the intake flow closed. */
  intakesBaseHref: string;
  /** Href for a section, for links rather than handlers. */
  hrefForTab: (tab: ClientTab) => string;
};

/** Binds the pure URL contract in `urlState.ts` to the router. */
export const useClientProfileUrlState = (): ClientProfileUrlApi => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname, search } = location;

  const state = useMemo(() => parseClientProfileSearch(search), [search]);

  const setTab = useCallback(
    (tab: ClientTab, focus?: ClientFocusParams) => {
      const next = buildClientProfileSearch(search, tab, focus);
      if (next === search) return;
      navigate({ pathname, search: next }, { replace: true });
    },
    [navigate, pathname, search]
  );

  const openCreateIntake = useCallback(
    (opts?: CreateIntakeOptions) => {
      navigate({ pathname, search: buildCreateIntakeSearch(search, opts) });
    },
    [navigate, pathname, search]
  );

  const openIntake = useCallback(
    (intakeId: string) => {
      navigate({ pathname, search: buildOpenIntakeSearch(search, intakeId) });
    },
    [navigate, pathname, search]
  );

  const openDocsOnlyIntakeForVisa = useCallback(
    (visaWorkflow: string) => {
      const next = buildDocsOnlyIntakeSearch(search, visaWorkflow);
      if (next === null) return;
      navigate({ pathname, search: next });
    },
    [navigate, pathname, search]
  );

  const intakesBaseHref = useMemo(
    () => `${pathname}${buildIntakesBaseSearch(search)}`,
    [pathname, search]
  );

  const hrefForTab = useCallback(
    (tab: ClientTab) => `${pathname}${buildClientProfileSearch(search, tab)}`,
    [pathname, search]
  );

  return {
    state,
    setTab,
    openCreateIntake,
    openIntake,
    openDocsOnlyIntakeForVisa,
    intakesBaseHref,
    hrefForTab
  };
};
